import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    grecaptcha?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => number;
      reset: (id?: number) => void;
      ready?: (cb: () => void) => void;
    };
  }
}

const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
let scriptPromise: Promise<void> | null = null;

function loadRecaptchaScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.grecaptcha?.render) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('reCAPTCHA failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('reCAPTCHA failed to load'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export interface ReCaptchaProps {
  /** v2 checkbox site key (portal settings recaptchaSiteKey). Renders nothing without one. */
  siteKey: string | null | undefined;
  /** Called with the token on success, or null when expired / errored. */
  onVerify: (token: string | null) => void;
  /** Called when Google's script can't load (no signal). */
  onLoadError?: () => void;
}

/**
 * Google reCAPTCHA v2 checkbox (same approach as frontend/ccms_v2/src/components/common/ReCaptcha.tsx,
 * but the site key comes from the portal settings). No npm dependency: api.js loads on demand.
 */
export function ReCaptcha({ siteKey, onVerify, onLoadError }: ReCaptchaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);
  const verifyRef = useRef(onVerify);
  verifyRef.current = onVerify;

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;
    loadRecaptchaScript()
      .then(() => {
        const tryRender = () => {
          if (cancelled || widgetIdRef.current !== null) return;
          const g = window.grecaptcha;
          if (!g?.render || !containerRef.current) {
            window.setTimeout(tryRender, 150);
            return;
          }
          widgetIdRef.current = g.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => verifyRef.current(token),
            'expired-callback': () => verifyRef.current(null),
            'error-callback': () => verifyRef.current(null),
          });
        };
        const g = window.grecaptcha;
        if (g?.ready) g.ready(tryRender);
        else tryRender();
      })
      .catch(() => {
        verifyRef.current(null);
        onLoadError?.();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) return null;
  return <div ref={containerRef} className="proc-recaptcha" />;
}
